// ── ErrorLogger.js ────────────────────────────────────────────────────
// Глобальний перехоплювач JS помилок.
// Імпортуй в index.js — один раз при старті додатку.
// ─────────────────────────────────────────────────────────────────────

import { SITE_URL } from './config/domain';

const API_URL   = `${SITE_URL}/api/errors`;
const _retryQ   = [];   // queue for failed sends
let   _retrying = false;

const MAX_QUEUE = 100;
let _count = 0;

// Retry failed errors every 30s (network may come back)
// Safari: skip when page is hidden (BFCache / background tab) to avoid waking the process
setInterval(async () => {
  if (document.visibilityState === 'hidden') return;
  if (_retrying || !_retryQ.length) return;
  _retrying = true;
  const item = _retryQ.shift();
  try {
    await fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: item });
  } catch { _retryQ.unshift(item); } // put back if still failing
  _retrying = false;
}, 30000);

// Reset counter every 5 min — do not block new errors permanently
// Safari: skip when page is hidden
setInterval(() => {
  if (document.visibilityState === 'hidden') return;
  _count = 0;
}, 5 * 60 * 1000);

// ── Helpers ──────────────────────────────────────────────────────────

function getEmail() {
  try { return localStorage.getItem('userEmail') || ''; } catch { return ''; }
}

// Витягує "ComponentName.methodName" з першого рядка стека
function extractComponent(stack = '') {
  const match = stack.match(/at ([A-Z]\w+)\.(\w+)/);
  if (match) return `${match[1]}.${match[2]}`;
  const line = (stack.split('\n').find(l => l.includes('at ')) || '').trim();
  return line.replace(/^at /, '').split('(')[0].trim().slice(0, 80);
}

// Хеш білду з chunk-файлу (main.xxxxxxxx.js)
function getBuildHash() {
  try {
    const scripts = Array.from(document.querySelectorAll('script[src]'));
    const main = scripts.find(s => s.src.includes('/static/js/main.'));
    if (!main) return '';
    const m = main.src.match(/main\.([a-f0-9]+)\.js/);
    return m ? m[1] : '';
  } catch { return ''; }
}

// ── Noise filter ──────────────────────────────────────────────────────
// Блокує шум який не є реальними помилками продакшену:
// 1. Мережеві помилки фонових prefetch-ів (CORS, 403)
// 2. React DevTools / внутрішні React попередження
// 3. Браузерні розширення
const NOISE_PATTERNS = [
  'Failed to fetch',
  'NetworkError',
  'Load failed',                      // Safari аналог Failed to fetch
  'settings_fetch_',
  'The above error occurred in the',  // React error boundary re-throw
  'Warning: Each child in a list',    // React key warning
  'Warning: An update to',            // React state update warning
  'Warning: Can\'t perform',          // setState on unmounted
  'ResizeObserver loop',              // Браузерний шум
  'Non-Error promise rejection',      // Не-Error об'єкти як rejection
  'Script error.',                    // Cross-origin scripts (extensions)
  'Extension context',                // Chrome extension errors
  'chrome-extension://',
  'moz-extension://',
  'Converting circular structure to JSON', // Сторонні розширення/трекери намагаються серіалізувати DOM-вузол (не наш код)
  'Object Not Found Matching Id',      // Класична помилка від розширень браузера (Skype click-to-call тощо), не наш код
];

function isNoise(msg = '', stack = '') {
  const combined = (msg + ' ' + stack).toLowerCase();
  if (NOISE_PATTERNS.some(p => (msg + stack).includes(p))) return true;
  // Блокуємо помилки з chrome/firefox extensions
  if (combined.includes('-extension://')) return true;
  return false;
}

// ── Core send ─────────────────────────────────────────────────────────
async function sendError({ type = 'js', level = 'error', message, stack = '', component }) {
  if (_count >= MAX_QUEUE) return;
  if (isNoise(message, stack)) return;
  _count++;

  const autoComponent = component || extractComponent(stack);

  try {
    const payload = JSON.stringify({
      action:      'log',
      type,
      level,
      message:     String(message).slice(0, 2000),
      stack:       String(stack).slice(0, 5000),
      component:   autoComponent.slice(0, 120),
      url:         window.location.href,
      user_email:  getEmail(),
      build:       getBuildHash(),
      viewport:    `${window.innerWidth}x${window.innerHeight}`,
      user_agent:  navigator.userAgent.slice(0, 300),
      page:        window.location.pathname,
    });

    // Safari < 17: keepalive not supported — use sendBeacon as fallback
    // sendBeacon works cross-origin and survives page unload
    const sent = typeof navigator.sendBeacon === 'function'
      ? navigator.sendBeacon(API_URL, new Blob([payload], { type: 'application/json' }))
      : false;

    if (!sent) {
      // Fallback: regular fetch, add to retry queue on failure
      fetch(API_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    payload,
      }).catch(() => {
        // Network failed — queue for retry
        if (_retryQ.length < 20) _retryQ.push(payload);
      });
    }
  } catch {
    // Silent fail
  }
}

// ── Global JS error handler ───────────────────────────────────────────
window.addEventListener('error', (event) => {
  const msg   = event.message || 'Unknown error';
  const stack = event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`;
  if (isNoise(msg, stack)) return;
  sendError({ type:'js', level:'error', message: msg, stack });
});

// ── Unhandled Promise rejections ──────────────────────────────────────
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const msg   = reason?.message || String(reason) || '';
  const stack = reason?.stack || '';
  if (isNoise(msg, stack)) return;
  sendError({ type:'js', level:'error', message: msg || 'Unhandled promise rejection', stack });
});

// ── Console.error override ────────────────────────────────────────────
// ВАЖЛИВО: перехоплює React warnings та console.error з компонентів
const _origError = console.error.bind(console);
console.error = (...args) => {
  _origError(...args);

  // Фільтр React warnings на рівні аргументів — до будь-якого join/format
  // React передає warning як перший аргумент рядок що починається з "Warning:"
  const firstArg = typeof args[0] === 'string' ? args[0] : '';
  if (firstArg.startsWith('Warning:')) return;
  if (firstArg.startsWith('Error: Warning:')) return;

  const message = args.map(a => {
    if (a instanceof Error) return a.message;
    if (typeof a === 'object') { try { return JSON.stringify(a); } catch { return String(a); } }
    return String(a);
  }).join(' ');

  const errObj = args.find(a => a instanceof Error);
  const stack  = errObj?.stack || '';

  // Подвійна перевірка — на випадок якщо Warning прийшов через Error об'єкт
  if (message.startsWith('Warning:')) return;
  if (isNoise(message, stack)) return;

  sendError({ type:'js', level:'error', message: message.slice(0, 2000), stack });
};

// ── Public API ────────────────────────────────────────────────────────
export function logError(error, component = '') {
  const msg   = error?.message || String(error);
  const stack = error?.stack || '';
  if (isNoise(msg, stack)) return;
  sendError({ type:'js', level:'error', message: msg, stack, component });
}

export function logWarning(message, component = '') {
  if (isNoise(message)) return;
  sendError({ type:'js', level:'warning', message, component });
}

export default sendError;
